import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, ShoppingBag, User, X, Lock } from "lucide-react";
import { CART_UPDATED_EVENT, getCartCount, readCart } from "@/utils/cart";
import AdminLoginModal from "./admin/components/AdminLoginModal.jsx";
import { useAdminAuth } from "./admin/context/AdminAuthContext.jsx";

export default function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [loginOpen, setLoginOpen] = useState(false);
  const { isAdmin } = useAdminAuth();

  const navLinks = [
    { label: "Home", to: "/" },
    { label: "Products", to: "/products" },
    { label: "Apparel", to: "/apparel" },
    { label: "Cart", to: "/cart" },
    { label: "About", to: "/about" },
    { label: "Newsletter", to: "/newsletter" }
  ];

  useEffect(() => {
    const syncCart = () => {
      setCartCount(getCartCount(readCart()));
    };

    syncCart();
    window.addEventListener(CART_UPDATED_EVENT, syncCart);
    window.addEventListener("storage", syncCart);

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, syncCart);
      window.removeEventListener("storage", syncCart);
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* ── Flat minimal header ── */}
      <header className="fixed inset-x-0 top-0 z-50 site-shell">
        <div className="page-wrap flex items-center justify-between h-14">
          {/* Brand wordmark */}
          <Link
            to="/"
            className="font-['Space_Mono'] text-[0.68rem] tracking-[0.24em] uppercase text-[var(--ink)] shrink-0"
          >
            Smple Product
          </Link>

          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary navigation">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="mono-label hover:text-[var(--ink)] transition-colors duration-150"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-4">
            <Link
              to="/account"
              className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors p-1"
              aria-label="Account"
            >
              <User size={15} />
            </Link>
            <Link
              to="/cart"
              className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors p-1 relative"
              aria-label="Open cart"
            >
              <ShoppingBag size={15} />
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-0.5 bg-[var(--brand)] text-[#09090b] text-[9px] font-bold font-['Space_Mono'] flex items-center justify-center">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </Link>
            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMenuOpen((prev) => !prev)}
              className="md:hidden text-[var(--muted)] hover:text-[var(--ink)] transition-colors p-1"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label="Toggle navigation"
            >
              {isMenuOpen ? <X size={15} /> : <Menu size={15} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown — full-width, dark, monotype links */}
        {isMenuOpen && (
          <nav
            id="mobile-menu"
            className="md:hidden border-t border-[var(--line)] bg-[var(--bg)]"
          >
            <div className="page-wrap py-8 flex flex-col gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="mono-label hover:text-[var(--ink)] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* Main content — offset by fixed header height */}
      <main className="pt-14">
        {children}
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-[var(--line)] bg-[var(--bg)]">
        <div className="page-wrap py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <Link
              to="/"
              className="font-['Space_Mono'] text-[0.65rem] tracking-[0.22em] uppercase text-[var(--ink)] block mb-2"
            >
              Smple Product
            </Link>
            <p className="text-[var(--muted)] text-xs font-['Space_Grotesk']">
              © {new Date().getFullYear()} Smple Product. All rights reserved.
            </p>
          </div>

          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            {[
              { label: "Products", to: "/products" },
              { label: "Apparel", to: "/apparel" },
              { label: "About", to: "/about" },
              { label: "Newsletter", to: "/newsletter" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-['Space_Mono'] text-[0.6rem] tracking-[0.14em] uppercase text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <button
            onClick={() => isAdmin ? (window.location.href = '/admin') : setLoginOpen(true)}
            className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors p-1.5 opacity-40 hover:opacity-100"
            aria-label="Admin access"
            title={isAdmin ? "Go to admin dashboard" : "Admin login"}
          >
            <Lock size={12} />
          </button>
        </div>
      </footer>

      <AdminLoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}